import { Card, CardContent, CardHeader, Chip, Grid, LinearProgress, Typography, Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { apiConstants, get, post } from "../../common/constants";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { FormProvider, RHFSelectField, RHFTextField } from "../../common/hook-form";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import { useForm } from "react-hook-form";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';

const POLL_INTERVAL_MS = 5000;

// Bulk monthly invoice generation: multi-select merchants -> one queued job per merchant
// (processed one at a time on the backend). Polls the job tracker and shows live
// per-merchant progress + outcome.
function BulkMerchantInvoice({ onJobComplete }) {
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const activeMerchants = merchants.filter((val) => val.is_deleted === "0")

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            merchants: Yup.array().min(1, "Please select at least one merchant").required("Please select merchants"),
            year: Yup.string().required("Please provide Year"),
            month: Yup.string().required("Please select Month")
        })),
        defaultValues: {
            merchants: [],
            year: '',
            month: ''
        }
    })
    const { handleSubmit, reset } = methods
    const { enqueueSnackbar } = useSnackbar()

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [jobId, setJobId] = useState(null)
    const [jobStatus, setJobStatus] = useState(null)
    const pollRef = useRef(null)

    const months = [{ label: 'January', Value: 1 }, { label: 'February', Value: 2 }, { label: 'March', Value: 3 }, { label: 'April', Value: 4 }, { label: 'May', Value: 5 },
    { label: 'June', Value: 6 }, { label: 'July', Value: 7 }, { label: 'August', Value: 8 }, { label: 'September', Value: 9 }, { label: 'October', Value: 10 }, { label: 'November', Value: 11 }, { label: 'December', Value: 12 }]

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
    }

    // On mount: if a bulk job is still running (e.g. page was refreshed midway), resume its progress.
    useEffect(() => {
        get(`${apiConstants.BULK_GENERATE_MERCHANT_INVOICE_RECENT}?days=2`).then((res) => {
            if (res?.data?.status !== 'SUCCESS') return
            const jobs = res?.data?.responseObj || []
            const running = jobs.find((j) => j?.fileProgress === 'IN_PROGRESS')
            if (running?.uuid) {
                setJobStatus(running)
                setJobId(running.uuid)
            }
        }).catch((err) => console.log(err))
    }, [])

    // Poll the tracker while a job is running; stop on terminal state.
    useEffect(() => {
        if (!jobId) return
        const fetchStatus = () => {
            get(`${apiConstants.BULK_GENERATE_MERCHANT_INVOICE_STATUS}?jobId=${jobId}`).then((res) => {
                if (res?.data?.status !== 'SUCCESS') return
                const tracker = res?.data?.responseObj
                setJobStatus(tracker)
                if (tracker?.fileProgress && tracker.fileProgress !== 'IN_PROGRESS') {
                    stopPolling()
                    if (onJobComplete) onJobComplete()
                }
            }).catch((err) => console.log(err))
        }
        fetchStatus()
        pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS)
        return stopPolling
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId])

    const onSubmit = (data) => {
        setIsSubmitting(true)
        setJobStatus(null)
        post(apiConstants.BULK_GENERATE_MERCHANT_INVOICE, {
            merchantRefs: data.merchants,
            year: Number(data.year),
            month: Number(data.month)
        }).then((res) => {
            if (res?.data?.status === 'SUCCESS') {
                const ack = res?.data?.responseObj
                enqueueSnackbar(`Bulk invoice job started for ${ack?.totalMerchants} merchants`, { variant: 'success' })
                setJobId(ack?.jobId)
                reset()
            } else {
                enqueueSnackbar(res?.data?.errorMsg, { variant: 'error' })
            }
            setIsSubmitting(false)
        }).catch((err) => {
            console.log(err)
            enqueueSnackbar("Failed to submit bulk invoice job", { variant: 'error' })
            setIsSubmitting(false)
        })
    }

    const executionStatus = jobStatus?.executionStatus || {}
    const totalTasks = executionStatus.totalTasks || 0
    const done = (executionStatus.success || 0) + (executionStatus.failed || 0)
    const progressPct = totalTasks > 0 ? Math.round((done * 100) / totalTasks) : 0
    const results = jobStatus?.metaDataObject?.results || []

    // Job context (survives page refresh — read from the tracker doc)
    const jobMeta = jobStatus?.metaDataObject || {}
    const jobMonthLabel = jobMeta.month ? (months.find((m) => m.Value === Number(jobMeta.month))?.label || jobMeta.month) : ''
    const jobCycle = jobMonthLabel && jobMeta.year ? `${jobMonthLabel} ${jobMeta.year}` : ''
    const selectedMerchants = jobMeta.merchants || []
    const jobStartedAt = jobStatus?.createdAt ? new Date(jobStatus.createdAt).toLocaleString() : ''

    const resultColumns = [
        { field: 'merchantName', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Merchant', flex: 1.5 },
        {
            field: 'outcome', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Outcome', flex: 0.8,
            renderCell: (params) => {
                const outcome = params?.row?.outcome || '';
                return <Chip size="small" color={outcome === 'SUCCESS' ? 'success' : 'error'} label={outcome} />
            },
        },
        { field: 'message', headerAlign: "left", align: 'left', hideSortIcons: true, headerName: 'Message', flex: 2.5 },
    ]

    return (
        <Grid sx={{ paddingTop: 2 }}>
            <Card>
                <CardHeader title='Bulk Generate Invoice Workings' />
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <RHFMultipleSelectField
                                    width="100%"
                                    name="merchants"
                                    search={true}
                                    label="Select Merchants"
                                    options={_.orderBy(activeMerchants, "name").map((val) => ({
                                        value: val.id,
                                        label: val.companyName ? `${val.name} (${val.companyName})` : val.name
                                    }))}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RHFSelectField name='month' label="Select Month" options={months.map((val) => ({
                                    value: val.Value,
                                    label: val.label,
                                }))} width='100%' />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <RHFTextField name='year' label="Year" />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <LoadingButton
                                    loading={isSubmitting}
                                    size='small'
                                    variant="contained" type="submit" sx={{ mt: 2 }}>Generate Invoices</LoadingButton>
                            </Grid>
                        </Grid>
                    </FormProvider>

                    {jobId && (
                        <Box sx={{ mt: 3 }}>
                            <Grid container spacing={1} sx={{ mb: 1 }}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        {jobCycle && <><b>Invoice Cycle:</b> {jobCycle} &nbsp;•&nbsp; </>}
                                        {jobMeta.requestedBy && <><b>Requested By:</b> {jobMeta.requestedBy} &nbsp;•&nbsp; </>}
                                        {jobStartedAt && <><b>Started:</b> {jobStartedAt}</>}
                                    </Typography>
                                </Grid>
                                {selectedMerchants.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                            <b>Merchants ({selectedMerchants.length}):</b>
                                            {selectedMerchants.map((m) => (
                                                <Chip key={m.merchantRef} size="small" variant="outlined" label={m.merchantName || m.merchantRef} />
                                            ))}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <LinearProgress variant="determinate" value={progressPct} sx={{ height: 8, borderRadius: 4 }} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2">
                                        {done} / {totalTasks} processed
                                        {executionStatus.failed > 0 ? ` (${executionStatus.failed} failed)` : ''}
                                        {jobStatus?.fileProgress && jobStatus.fileProgress !== 'IN_PROGRESS' &&
                                            <Chip size="small" sx={{ ml: 1 }}
                                                color={jobStatus.fileProgress === 'COMPLETED' ? 'success' : 'error'}
                                                label={jobStatus.fileProgress} />}
                                    </Typography>
                                </Grid>
                            </Grid>
                            {results.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <ProzoDataGrid
                                        columns={resultColumns}
                                        autoHeight={true}
                                        rows={results.map((r, index) => ({ ...r, id: r.merchantRef || index }))}
                                        pagination={false}
                                        hideFooterPagination={true}
                                        rowHeight={45}
                                        rowCount={results.length}
                                        getRowId={(row) => row.id}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Grid>
    )
}

export default BulkMerchantInvoice;
